import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDream } from '@/hooks/useDream'
import { useProUpgrade } from '@/hooks/useProUpgrade'
import { useLanguage } from '@/hooks/useLanguage'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useConfirmContext } from '@/store/contexts/ConfirmContext'
import { useLoading } from '@/store/contexts/LoadingContext'
import { t } from '@/utils/i18n'
import { Dream, CreateDreamDto, UpdateDreamDto } from '@/store/types/dream.types'

/** 꿈 생성 폼: 예산은 비울 수 있음(빈 값이면 API 호출 시 0 전송) */
type CreateDreamFormState = Omit<CreateDreamDto, 'budget'> & { budget: number | null }
/** 꿈 수정 폼: 예산은 비울 수 있음(빈 값이면 API 호출 시 0 전송) */
type UpdateDreamFormState = Omit<UpdateDreamDto, 'budget'> & { budget: number | null }
import DreamCard from '@/components/planner/DreamCard/DreamCard'
import Modal from '@/components/common/Modal/Modal'
import Button from '@/components/common/Button/Button'
import { Input } from '@/components/common/Input/Input'
import Skeleton from '@/components/common/Skeleton/Skeleton'
import EmptyState from '@/components/common/EmptyState/EmptyState'
import { IconWarning, IconDream, IconPlus } from '@/components/icons/Icons'
import './Dreams.css'

function Dreams() {
  const navigate = useNavigate()
  const { showToast } = useToastContext()
  const { confirm } = useConfirmContext()
  const {
    dreams,
    loading,
    error,
    fetchDreams,
    fetchDreamById,
    deleteDream,
    createDream,
    updateDream,
  } = useDream()
  const { language } = useLanguage()
  const { isPro, handleRestrictionError, showProUpgradeDialog } = useProUpgrade()
  const { startLoading, stopLoading } = useLoading()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedDream, setSelectedDream] = useState<Dream | null>(null)
  const [isCreatingDream, setIsCreatingDream] = useState(false)
  const [isUpdatingDream, setIsUpdatingDream] = useState(false)
  const [isDeletingDream, setIsDeletingDream] = useState(false)
  const [isFetchingDreamForEdit, setIsFetchingDreamForEdit] = useState(false)

  const [createForm, setCreateForm] = useState<CreateDreamFormState>({
    dreamName: '',
    dreamDesc: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    budget: null,
    categories: [],
  })

  const [editForm, setEditForm] = useState<UpdateDreamFormState>({
    dreamName: '',
    dreamDesc: '',
    startDate: '',
    endDate: '',
    budget: null,
    categories: [],
    completionRate: null,
  })





  // 페이지 진입 시 최신 꿈 목록 로드 (아이템 추가 등 타 페이지 변경사항 반영용)
  useEffect(() => {
    fetchDreams()
  }, [fetchDreams])

  // 메뉴 이동 스피너: 꿈 목록 로딩·렌더 완료 후 종료(페인트 후 stopLoading)
  useEffect(() => {
    if (!loading) {
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          stopLoading()
        })
      })
      return () => cancelAnimationFrame(rafId)
    }
  }, [loading, stopLoading])

  const handleCreateDream = async () => {
    // 폼 검증
    if (!createForm.dreamName.trim()) {
      showToast(t('dreams.dreamNameRequired', language) || '꿈 이름을 입력해주세요.', 'error')
      return
    }

    if (new Date(createForm.startDate) > new Date(createForm.endDate)) {
      showToast(t('messages.invalidDateRange', language) || '시작일은 종료일보다 이전이어야 합니다.', 'error')
      return
    }

    if (createForm.budget != null && createForm.budget < 0) {
      showToast(t('messages.invalidBudget', language) || '예산은 0 이상이어야 합니다.', 'error')
      return
    }

    try {
      setIsCreatingDream(true)
      startLoading()
      await createDream({
        ...createForm,
        dreamName: createForm.dreamName.trim(),
        dreamDesc: createForm.dreamDesc.trim(),
        budget: createForm.budget ?? 0,
      })
      
      // 토스트를 먼저 표시해 목록 갱신과 동기화되도록 함
      showToast(t('messages.createSuccess', language) || '꿈이 생성되었습니다.', 'success')
      
      // 응답 받으면 모달 닫기 및 상태 초기화
      setIsCreateModalOpen(false)
      setCreateForm({
        dreamName: '',
        dreamDesc: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
        budget: null,
        categories: [],
      })
      
      // 꿈 목록 새로고침
      await fetchDreams()
      setIsCreatingDream(false)
    } catch (error: any) {
      // 에러 발생 시 먼저 스피너를 끄고 나서 다이얼로그 표시
      setIsCreatingDream(false)
      const errorMessage = error?.response?.data?.message || error?.message || t('messages.dreamCreateFailed', language)
      const handled = await handleRestrictionError(error)
      if (!handled) {
        showToast(errorMessage, 'error')
      }
    } finally {
      stopLoading()
    }
  }

  const handleEditDream = async (dream: Dream) => {
    setSelectedDream(dream)
    setEditForm({
      dreamName: dream.dreamName,
      dreamDesc: dream.dreamDesc,
      startDate: dream.startDate,
      endDate: dream.endDate,
      budget: dream.budget ?? null,
      categories: [],
      completionRate: dream.completionRate,
    })
    setIsEditModalOpen(true)
    setIsFetchingDreamForEdit(true)
    startLoading()

    try {
      const fetched = await fetchDreamById(dream.dreamId)
      if (fetched) {
        setEditForm({
          dreamName: fetched.dreamName,
          dreamDesc: fetched.dreamDesc,
          startDate: fetched.startDate,
          endDate: fetched.endDate,
          budget: fetched.budget ?? null,
          categories: [],
          completionRate: fetched.completionRate,
        })
        setSelectedDream(fetched)
      }
    } catch (err) {
      console.warn('fetchDreamById failed in handleEditDream', err)
    } finally {
      setIsFetchingDreamForEdit(false)
      stopLoading()
    }
  }

  const handleUpdateDream = async () => {
    if (!selectedDream) return
    try {
      setIsUpdatingDream(true)
      startLoading()
      await updateDream(selectedDream.dreamId, {
        ...editForm,
        budget: editForm.budget ?? 0,
        completionRate: editForm.completionRate,
      })
      
      // 응답 받으면 모달 닫기 및 상태 초기화
      setIsEditModalOpen(false)
      setSelectedDream(null)
      
      // 꿈 목록 새로고침
      await fetchDreams()
      
      showToast(t('messages.updateSuccess', language), 'success')
    } catch (error) {
      showToast(t('messages.updateFailed', language), 'error')
    } finally {
      setIsUpdatingDream(false)
      stopLoading()
    }
  }

  const handleDeleteDream = async (dreamId: string) => {
    if (!dreamId) return

    // 삭제 확인 다이얼로그 표시
    const confirmed = await confirm({
      message: t('messages.deleteConfirm', language) || '정말 삭제하시겠습니까?',
      variant: 'danger',
    })

    if (!confirmed) return

    setIsDeletingDream(true)
    try {
      await deleteDream(dreamId)
      await fetchDreams()
      showToast(t('messages.deleteSuccess', language), 'success')
    } catch (error) {
      console.error(error)
      const errorMessage = (error as any)?.response?.data?.message || (error as any)?.message || t('messages.deleteFailed', language)
      showToast(errorMessage, 'error')
    } finally {
      setIsDeletingDream(false)
    }
  }

  if (loading && !isDeletingDream) {
    return (
      <div className="dreams" style={{ position: 'relative' }}>
        <div className="dreams__header">
          <Skeleton height={32} width={150} />
          <Skeleton height={40} width={120} />
        </div>
        <div className="dreams__grid dreams__grid--skeleton">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="dreams__skeleton-card">
              <Skeleton height={200} variant="rectangular" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="dreams">
        <EmptyState
          icon={<IconWarning size={32} />}
          title={t('common.error', language)}
          description={error}
          actionLabel={t('common.refresh', language)}
          onAction={() => window.location.reload()}
        />
      </div>
    )
  }

  // console.debug('[Dreams] 렌더링, isDeletingDream:', isDeletingDream)
  
  return (
    <div className="dreams">

      <button
        type="button"
        className="fab"
        disabled={isDeletingDream}
        onClick={async () => {
          if (!isPro && dreams.length >= 5) {
            await showProUpgradeDialog()
            return
          }
          setIsCreateModalOpen(true)
        }}
        title={language === 'ko' ? '꿈 생성' : 'Create Dream'}
        aria-label={language === 'ko' ? '꿈 생성' : 'Create Dream'}
      >
        <IconPlus size={24} />
      </button>
      <div className="dreams__grid">
        {dreams.length === 0 ? (
          <EmptyState
            icon={<IconDream size={32} />}
            title={t('dreams.noDreams', language)}
            description={t('dreams.noDreamsDesc', language) || '첫 번째 꿈을 만들어보세요!'}
          />
        ) : (
          dreams.map((dream) => (
            <DreamCard
              key={dream.dreamId}
              dream={dream}
              onEdit={handleEditDream}
              onDelete={handleDeleteDream}
              onAccount={(did) => navigate(`/accounts?dreamId=${did}`)}
            />
          ))
        )}
      </div>

      {/* 꿈 생성 모달 */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isCreatingDream && setIsCreateModalOpen(false)}
        title={t('dreams.createTitle', language)}
        size="md"
      >
        <div className={`dreams__modal-content ${isCreatingDream ? 'dreams__modal-content--loading' : ''}`}>
          <Input
            label={t('dreams.dreamName', language)}
            value={createForm.dreamName}
            onChange={(e) =>
              setCreateForm({ ...createForm, dreamName: e.target.value })
            }
            placeholder={t('dreams.dreamNamePlaceholder', language)}
            required
          />
          <Input
            label={t('dreams.dreamDesc', language)}
            value={createForm.dreamDesc}
            onChange={(e) =>
              setCreateForm({ ...createForm, dreamDesc: e.target.value })
            }
            placeholder={t('dreams.dreamDescPlaceholder', language)}
          />
          <div className="dreams__form-row dreams__form-row--dates">
            <Input
              label={t('dreams.startDate', language)}
              type="date"
              value={createForm.startDate}
              onChange={(e) =>
                setCreateForm({ ...createForm, startDate: e.target.value })
              }
            />
            <Input
              label={t('dreams.endDate', language)}
              type="date"
              value={createForm.endDate}
              onChange={(e) =>
                setCreateForm({ ...createForm, endDate: e.target.value })
              }
            />
          </div>
          <Input
            label={t('dreams.budgetLabel', language)}
            type="number"
            min={0}
            value={createForm.budget == null ? '' : String(createForm.budget)}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || /^\d+$/.test(value)) {
                setCreateForm({
                  ...createForm,
                  budget: value === '' ? null : Math.max(0, parseInt(value, 10)),
                })
              }
            }}
            placeholder={t('dreams.budgetPlaceholder', language)}
          />
          {/* 그룹 관리는 꿈 카드의 '그룹' 버튼으로 이동 */}
          <div className="dreams__modal-actions">
            <Button 
              onClick={handleCreateDream}
              disabled={isCreatingDream}
            >
              {isCreatingDream ? (
                <>
                  <span className="dreams__loading-spinner"></span>
                  {t('dreams.creating', language) || '생성중입니다...'}
                </>
              ) : (
                t('common.create', language)
              )}
            </Button>
          </div>
        </div>
      </Modal>


      {/* 꿈 수정 모달 */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isUpdatingDream && !isFetchingDreamForEdit && setIsEditModalOpen(false)}
        title={t('dreams.editTitle', language)}
        size="md"
      >
        <div className={`dreams__modal-content ${(isUpdatingDream || isFetchingDreamForEdit) ? 'dreams__modal-content--loading' : ''}`}>
          <Input
            label={t('dreams.dreamName', language)}
            value={editForm.dreamName}
            onChange={(e) =>
              setEditForm({ ...editForm, dreamName: e.target.value })
            }
            required
          />
          <Input
            label={t('dreams.dreamDesc', language)}
            value={editForm.dreamDesc}
            onChange={(e) =>
              setEditForm({ ...editForm, dreamDesc: e.target.value })
            }
          />
          <div className="dreams__form-row dreams__form-row--dates">
            <Input
              label={t('dreams.startDate', language)}
              type="date"
              value={editForm.startDate}
              onChange={(e) =>
                setEditForm({ ...editForm, startDate: e.target.value })
              }
            />
            <Input
              label={t('dreams.endDate', language)}
              type="date"
              value={editForm.endDate}
              onChange={(e) =>
                setEditForm({ ...editForm, endDate: e.target.value })
              }
            />
          </div>
          <Input
            label={t('dreams.budgetLabel', language)}
            type="number"
            min={0}
            value={editForm.budget == null ? '' : String(editForm.budget)}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || /^\d+$/.test(value)) {
                setEditForm({
                  ...editForm,
                  budget: value === '' ? null : Math.max(0, parseInt(value, 10)),
                })
              }
            }}
            placeholder={t('dreams.budgetPlaceholder', language)}
          />

          <div className="dreams__form-group">
            <label>{t('dreams.status', language)}</label>
            <select
              value={(() => {
                const raw = editForm.completionRate ?? 0
                const normalized = raw > 1 ? raw / 100 : raw
                return normalized >= 1 ? 'completed' : normalized > 0 ? 'inProgress' : 'preparing'
              })()}
              disabled
              className="dreams__status-select"
              style={{
                backgroundColor: 'var(--color-background)',
                cursor: 'not-allowed',
                opacity: 0.7
              }}
            >
              <option value="preparing">{t('common.preparing', language)}</option>
              <option value="inProgress">{t('common.inProgress', language)}</option>
              <option value="completed">{t('common.completed', language)}</option>
            </select>
          </div>
              <div className="dreams__modal-actions">
                <Button 
                  onClick={handleUpdateDream}
                  disabled={isUpdatingDream}
                >
                  {isUpdatingDream ? (
                    <>
                      <span className="dreams__loading-spinner"></span>
                      {t('messages.processing', language) || '처리중입니다...'}
                    </>
                  ) : (
                    t('common.edit', language)
                  )}
                </Button>
              </div>
        </div>
      </Modal>

    </div>
  )
}

export default Dreams



